import { useState } from "react";
import type { MapPin } from "../types";
import { mapsLinks } from "../lib/mapsLinks";
import { usePlacePhotos, useResolvedPinPhoto } from "../hooks/usePlacePhotos";
import { pinMayHavePhoto } from "../lib/googlePlaces";

type PinLike = Pick<
  MapPin,
  | "id"
  | "name"
  | "lat"
  | "lng"
  | "mapsQuery"
  | "placeId"
  | "imageUrl"
  | "imageAlt"
  | "imageCredit"
>;

interface Props {
  pin: PinLike;
  /** Prefer compact strip + rating for schedule/list rows */
  compact?: boolean;
  className?: string;
}

function Stars({ rating }: { rating: number }) {
  const rounded = Math.round(rating * 10) / 10;
  return (
    <span className="place-rating" title={`${rounded} out of 5`}>
      <span className="place-rating-star" aria-hidden>
        ★
      </span>
      <strong>{rounded.toFixed(1)}</strong>
    </span>
  );
}

/**
 * In-app place detail: rating + review count under title area, photo strip,
 * collapsible reviews, Maps deep link as secondary text action.
 * Uses the existing PlacePhotosProvider enrichment path only.
 */
export function PlaceDetail({ pin, compact = false, className = "" }: Props) {
  const { getPhoto } = usePlacePhotos();
  const enrichment = getPhoto(pin.id);
  const resolved = useResolvedPinPhoto(pin);
  const [reviewsOpen, setReviewsOpen] = useState(false);

  if (!pinMayHavePhoto(pin) && !enrichment) return null;

  const links = mapsLinks({
    name: pin.name,
    lat: pin.lat,
    lng: pin.lng,
    mapsQuery: pin.mapsQuery,
    placeId: pin.placeId ?? enrichment?.placeId,
  });

  const stripUrls =
    enrichment?.mediaUrls && enrichment.mediaUrls.length > 0
      ? enrichment.mediaUrls
      : resolved.url
        ? [resolved.url]
        : pin.imageUrl
          ? [pin.imageUrl]
          : [];

  const rating = enrichment?.rating;
  const count = enrichment?.userRatingCount;
  const reviews = enrichment?.reviews ?? [];
  const credit = enrichment?.credit ?? resolved.credit ?? pin.imageCredit;

  return (
    <div
      className={`place-detail${compact ? " compact" : ""} ${className}`.trim()}
    >
      {(rating != null || (count != null && count > 0)) && (
        <p className="place-rating-row">
          {rating != null && <Stars rating={rating} />}
          {count != null && count > 0 && (
            <span className="place-review-count">
              {count.toLocaleString()} review{count === 1 ? "" : "s"}
            </span>
          )}
        </p>
      )}

      {stripUrls.length > 0 && (
        <div
          className="place-photo-strip"
          role="list"
          aria-label={`Photos of ${pin.name}`}
        >
          {stripUrls.map((url, i) => (
            <figure key={`${url}-${i}`} className="place-photo-strip-item" role="listitem">
              <img
                src={url}
                alt={
                  i === 0
                    ? (pin.imageAlt ?? pin.name)
                    : `${pin.name} photo ${i + 1}`
                }
                loading="lazy"
                decoding="async"
                onError={
                  enrichment && i === 0 && resolved.source === "places"
                    ? resolved.onError
                    : undefined
                }
              />
            </figure>
          ))}
        </div>
      )}

      {credit && <p className="place-photo-credit muted">{credit}</p>}

      {reviews.length > 0 && (
        <div className="place-reviews">
          <button
            type="button"
            className="place-reviews-toggle"
            aria-expanded={reviewsOpen}
            onClick={() => setReviewsOpen((v) => !v)}
          >
            {reviewsOpen ? "Hide reviews" : `Read reviews (${reviews.length})`}
          </button>
          {reviewsOpen && (
            <ul className="place-reviews-list">
              {reviews.map((r, i) => (
                <li key={`${r.authorName}-${i}`}>
                  <div className="place-review-meta">
                    <strong>{r.authorName}</strong>
                    {r.rating != null && (
                      <span className="muted">★ {r.rating}</span>
                    )}
                    {r.relativeTime && (
                      <span className="muted">{r.relativeTime}</span>
                    )}
                  </div>
                  <p>{r.text}</p>
                </li>
              ))}
            </ul>
          )}
        </div>
      )}

      <a
        className="place-maps-text-link"
        href={links.primary}
        target="_blank"
        rel="noopener noreferrer"
      >
        Open in {links.appleFirst ? "Apple Maps" : "Google Maps"} →
      </a>
    </div>
  );
}
